import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { ttiSettingsService } from '../services/ttiSettingsService';
import { ttiApplicationKeysService } from '../services/ttiApplicationKeysService';
import { openAISettingsService, OpenAISettingsStatus } from '../services/openAISettingsService';
import Layout from '../components/Layout';
import { 
  User, 
  TTISettingsResponse,
  TTIApplicationKey,
  TTIApplicationKeyCreate
} from '../types';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [gatewaySettings, setGatewaySettings] = useState<TTISettingsResponse | null>(null);
  const [gatewayApiKey, setGatewayApiKey] = useState('');
  const [gatewayRegion, setGatewayRegion] = useState('eu1');
  const [savingGatewaySettings, setSavingGatewaySettings] = useState(false);

  const [openAISettings, setOpenAISettings] = useState<OpenAISettingsStatus | null>(null);
  const [openAIKey, setOpenAIKey] = useState('');
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [savingOpenAIKey, setSavingOpenAIKey] = useState(false);

  // TTI Application Keys state
  const [applicationKeys, setApplicationKeys] = useState<TTIApplicationKey[]>([]);
  const [loadingAppKeys, setLoadingAppKeys] = useState(false);
  const [showAddAppKeyModal, setShowAddAppKeyModal] = useState(false);
  const [addAppKeyLoading, setAddAppKeyLoading] = useState(false);
  const [addAppKeyError, setAddAppKeyError] = useState('');
  const [verifyingKeyId, setVerifyingKeyId] = useState<number | null>(null);
  const [appKeyFormData, setAppKeyFormData] = useState<TTIApplicationKeyCreate>({
    name: '',
    application_id: '',
    api_key: '',
    region: 'eu1'
  });

  useEffect(() => {
    // Load data once on mount - loadData is intentionally not in deps
    // as we only want to run this effect once, not on every loadData recreation
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      
      if (currentUser.is_admin) {
        await Promise.all([loadGatewaySettings(), loadApplicationKeys(), loadOpenAISettings()]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const loadGatewaySettings = async () => {
    try {
      const settings = await ttiSettingsService.getSettings();
      setGatewaySettings(settings);
      if (settings.region) setGatewayRegion(settings.region);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.gateway.loadFailed'));
    }
  };

  const handleSaveGatewaySettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingGatewaySettings(true);
    try {
      const settings = await ttiSettingsService.createOrUpdateSettings({
        api_key: gatewayApiKey.trim(),
        region: gatewayRegion,
      });
      setGatewaySettings(settings);
      setGatewayApiKey('');
      setSuccess(t('settings.gateway.saved'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.gateway.saveFailed'));
    } finally {
      setSavingGatewaySettings(false);
    }
  };

  const handleRemoveGatewaySettings = async () => {
    if (!window.confirm(t('settings.gateway.confirmRemove'))) return;
    setError('');
    setSuccess('');
    try {
      await ttiSettingsService.deleteSettings();
      setGatewaySettings({
        is_configured: false,
        id: null,
        region: null,
        is_verified: false,
        last_verified_at: null,
        has_api_key: false,
        created_at: null,
        updated_at: null,
      });
      setGatewayRegion('eu1');
      setSuccess(t('settings.gateway.removed'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.gateway.removeFailed'));
    }
  };

  const loadOpenAISettings = async () => {
    try {
      setOpenAISettings(await openAISettingsService.getSettings());
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.openAI.loadFailed'));
    }
  };

  const handleSaveOpenAIKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSavingOpenAIKey(true);
    try {
      const updated = await openAISettingsService.updateKey(openAIKey);
      setOpenAISettings(updated);
      setOpenAIKey('');
      setShowOpenAIKey(false);
      setSuccess(t('settings.openAI.saved'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.openAI.saveFailed'));
    } finally {
      setSavingOpenAIKey(false);
    }
  };

  const handleRemoveOpenAIKey = async () => {
    if (!window.confirm(t('settings.openAI.confirmRemove'))) return;
    setError('');
    setSuccess('');
    try {
      const result = await openAISettingsService.deleteKey();
      await loadOpenAISettings();
      setSuccess(result.fallback_to_environment
        ? t('settings.openAI.removedWithFallback')
        : t('settings.openAI.removed'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('settings.openAI.removeFailed'));
    }
  };

  const loadApplicationKeys = async () => {
    setLoadingAppKeys(true);
    try {
      const response = await ttiApplicationKeysService.listApplicationKeys();
      setApplicationKeys(response.application_keys);
    } catch (err: any) {
      console.error('Failed to load application keys:', err);
    } finally {
      setLoadingAppKeys(false);
    }
  };

  // Application Keys handlers
  const handleOpenAddAppKeyModal = () => {
    setAppKeyFormData({
      name: '',
      application_id: '',
      api_key: '',
      region: 'eu1'
    });
    setAddAppKeyError('');
    setShowAddAppKeyModal(true);
  };

  const handleCloseAddAppKeyModal = () => {
    setShowAddAppKeyModal(false);
    setAddAppKeyError('');
  };

  const handleAddApplicationKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddAppKeyError('');
    setAddAppKeyLoading(true);

    try {
      await ttiApplicationKeysService.createApplicationKey(appKeyFormData);
      handleCloseAddAppKeyModal();
      setSuccess(`Application "${appKeyFormData.name}" added successfully!`);
      await loadApplicationKeys();
    } catch (err: any) {
      setAddAppKeyError(err.response?.data?.detail || 'Failed to add application key');
    } finally {
      setAddAppKeyLoading(false);
    }
  };

  const handleVerifyApplicationKey = async (keyId: number) => {
    setVerifyingKeyId(keyId);
    setError('');
    setSuccess('');

    try {
      const result = await ttiApplicationKeysService.verifyApplicationKey(keyId);
      if (result.is_valid) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
      await loadApplicationKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to verify application key');
    } finally {
      setVerifyingKeyId(null);
    }
  };

  const handleDeleteApplicationKey = async (keyId: number, appName: string) => {
    if (!window.confirm(t('settings.ttiApplications.confirmDelete', { name: appName }))) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await ttiApplicationKeysService.deleteApplicationKey(keyId);
      setSuccess(`Application key for "${appName}" deleted successfully`);
      await loadApplicationKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete application key');
    }
  };

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (!user?.is_admin) {
    return (
      <Layout user={user || undefined}>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {t('common.noPermission')}
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user || undefined}>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>

        {/* Error/Success Messages */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded">
            {success}
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.gateway.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('settings.gateway.description')}</p>
          </div>

          <div className="mb-4 text-sm">
            <span className={`px-2 py-1 rounded-full ${
              gatewaySettings?.is_configured
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {gatewaySettings?.is_configured
                ? t('settings.gateway.configured')
                : t('settings.gateway.notConfigured')}
            </span>
          </div>

          <form onSubmit={handleSaveGatewaySettings} className="max-w-2xl space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {gatewaySettings?.is_configured
                  ? t('settings.gateway.replaceKey')
                  : t('settings.gateway.apiKey')}
              </label>
              <input
                type="password"
                value={gatewayApiKey}
                onChange={(e) => setGatewayApiKey(e.target.value)}
                placeholder="NNSXS..."
                autoComplete="new-password"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">{t('settings.gateway.keyHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.gateway.region')}
              </label>
              <select
                value={gatewayRegion}
                onChange={(e) => setGatewayRegion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {['eu1', 'eu2', 'nam1', 'au1', 'as1', 'as2'].map((region) => (
                  <option key={region} value={region}>{region.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={savingGatewaySettings || !gatewayApiKey.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingGatewaySettings ? t('settings.gateway.saving') : t('settings.gateway.save')}
              </button>
              {gatewaySettings?.is_configured && (
                <button
                  type="button"
                  onClick={handleRemoveGatewaySettings}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                >
                  {t('settings.gateway.remove')}
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('settings.openAI.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">{t('settings.openAI.description')}</p>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            <span className={`px-2 py-1 rounded-full ${
              openAISettings?.is_configured
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {openAISettings?.is_configured ? t('settings.openAI.configured') : t('settings.openAI.notConfigured')}
            </span>
            {openAISettings?.key_hint && <span className="font-mono text-gray-600">{openAISettings.key_hint}</span>}
            {openAISettings?.source === 'environment' && (
              <span className="text-gray-500">{t('settings.openAI.environmentSource')}</span>
            )}
            {openAISettings?.source === 'database' && (
              <span className="text-gray-500">{t('settings.openAI.savedSource')}</span>
            )}
          </div>

          <form onSubmit={handleSaveOpenAIKey} className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {openAISettings?.is_configured ? t('settings.openAI.replaceKey') : t('settings.openAI.apiKey')}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <input
                  type={showOpenAIKey ? 'text' : 'password'}
                  value={openAIKey}
                  onChange={(e) => setOpenAIKey(e.target.value)}
                  placeholder="sk-..."
                  autoComplete="new-password"
                  required
                  minLength={8}
                  className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowOpenAIKey(value => !value)}
                  className="absolute inset-y-0 right-0 px-3 text-xs text-gray-500 hover:text-gray-700"
                >
                  {showOpenAIKey ? t('settings.openAI.hide') : t('settings.openAI.show')}
                </button>
              </div>
              <button
                type="submit"
                disabled={savingOpenAIKey || !openAIKey.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingOpenAIKey ? t('settings.openAI.saving') : t('settings.openAI.save')}
              </button>
              {openAISettings?.source === 'database' && (
                <button
                  type="button"
                  onClick={handleRemoveOpenAIKey}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50"
                >
                  {t('settings.openAI.remove')}
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-gray-500">{t('settings.openAI.securityHint')}</p>
          </form>
        </div>

        {/* TTI Application Keys Section - Primary Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('settings.ttiApplications.title')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('settings.ttiApplications.description')}
              </p>
            </div>
            <button
              onClick={handleOpenAddAppKeyModal}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 self-start sm:self-auto"
            >
              {t('settings.ttiApplications.addApplication')}
            </button>
          </div>

          {loadingAppKeys ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : applicationKeys.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('settings.ttiApplications.noApplications')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('settings.ttiApplications.noApplicationsHint')}
              </p>
              <button
                onClick={handleOpenAddAppKeyModal}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                {t('settings.ttiApplications.addFirstApplication')}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.ttiApplications.columns.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.ttiApplications.columns.applicationId')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.ttiApplications.columns.region')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.ttiApplications.columns.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.ttiApplications.columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applicationKeys.map((appKey) => (
                    <tr key={appKey.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{appKey.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {appKey.application_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {appKey.region.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          appKey.is_verified 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {appKey.is_verified ? t('common.verified') : t('common.notVerified')}
                        </span>
                        {appKey.last_verified_at && (
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(appKey.last_verified_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => handleVerifyApplicationKey(appKey.id)}
                          disabled={verifyingKeyId === appKey.id}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        >
                          {verifyingKeyId === appKey.id ? t('settings.ttiApplications.verifying') : t('settings.ttiApplications.verify')}
                        </button>
                        <button
                          onClick={() => handleDeleteApplicationKey(appKey.id, appKey.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('settings.ttiApplications.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add Application Key Modal */}
      {showAddAppKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('settings.ttiApplications.addModal.title')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.ttiApplications.addModal.description')}
            </p>
            <form onSubmit={handleAddApplicationKey} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.ttiApplications.addModal.displayName')} *
                </label>
                <input
                  type="text"
                  value={appKeyFormData.name}
                  onChange={(e) => setAppKeyFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('settings.ttiApplications.addModal.displayNamePlaceholder')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.ttiApplications.addModal.displayNameHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.ttiApplications.addModal.applicationId')} *
                </label>
                <input
                  type="text"
                  value={appKeyFormData.application_id}
                  onChange={(e) => setAppKeyFormData(prev => ({ 
                    ...prev, 
                    application_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                  }))}
                  placeholder={t('settings.ttiApplications.addModal.applicationIdPlaceholder')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.ttiApplications.addModal.applicationIdHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.ttiApplications.addModal.apiKey')} *
                </label>
                <input
                  type="password"
                  value={appKeyFormData.api_key}
                  onChange={(e) => setAppKeyFormData(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder={t('settings.ttiApplications.addModal.apiKeyPlaceholder')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('settings.ttiApplications.addModal.apiKeyHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.ttiApplications.addModal.region')} *
                </label>
                <select
                  value={appKeyFormData.region}
                  onChange={(e) => setAppKeyFormData(prev => ({ ...prev, region: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="eu1">{t('settings.ttiApplications.addModal.regions.eu1')}</option>
                  <option value="eu2">{t('settings.ttiApplications.addModal.regions.eu2')}</option>
                  <option value="au1">{t('settings.ttiApplications.addModal.regions.au1')}</option>
                  <option value="as1">{t('settings.ttiApplications.addModal.regions.as1')}</option>
                  <option value="as2">{t('settings.ttiApplications.addModal.regions.as2')}</option>
                  <option value="nam1">{t('settings.ttiApplications.addModal.regions.nam1')}</option>
                </select>
              </div>
              
              {addAppKeyError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                  {addAppKeyError}
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseAddAppKeyModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addAppKeyLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {addAppKeyLoading ? t('settings.ttiApplications.addModal.adding') : t('settings.ttiApplications.addModal.addButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Settings;
